import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp,
  updateDoc
} from 'firebase/firestore';
import { db } from './firebase';

// ==================== TIPOS ====================

export interface ExamMetadata {
  institution: string; // Ex: INSS, PF, TJ-RJ
  role: string;        // Ex: Técnico, Analista, Agente
  year: number;        // Ex: 2024
  banca: string;       // Ex: Cebraspe, FGV
  level?: 'medio' | 'superior';
  active: boolean;
  examDate?: string;   // Data da prova (YYYY-MM-DD)
  aliases?: string[];  // Siglas/apelidos: ex: PF, PRF, PC, PM
}

const normalizeText = (s: string = "") =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

export interface ExamSubject {
  name: string;
  topics: string[];
  weight?: number; // Peso da matéria (opcional)
}

export interface Exam {
  id: string; // ID composto: institution_role_year (ex: inss_tecnico_2024)
  metadata: ExamMetadata;
  subjects: ExamSubject[];
  createdAt: any;
  updatedAt: any;
}

// ==================== FUNÇÕES ====================

/**
 * Salva ou atualiza um edital no banco de dados
 */
export const saveExam = async (exam: Omit<Exam, 'createdAt' | 'updatedAt'>) => {
  const examRef = doc(db, 'exams', exam.id);
  
  await setDoc(examRef, {
    ...exam,
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp() // Em um update real, deveríamos preservar o createdAt original
  }, { merge: true }); // merge true para não sobrescrever createdAt se já existir
};

/**
 * Busca editais disponíveis para o onboarding (autocomplete)
 * Pode filtrar por termo de busca
 */
const INSTITUTION_ALIASES: Record<string, string[]> = {
  "policia federal": ["pf", "dpf", "polícia federal"],
  "policia civil": ["pc", "polícia civil"],
  "policia militar": ["pm", "polícia militar"],
  "policia rodoviaria federal": ["prf", "polícia rodoviária federal"],
  "tribunal de justica de sao paulo": ["tjsp", "tj-sp", "tj sp"],
  "tribunal de justica do rio de janeiro": ["tjrj", "tj-rj", "tj rj"],
  "tribunal regional federal": ["trf"],
  "tribunal regional do trabalho": ["trt"],
  "banco central do brasil": ["bacen", "bcb", "bc"],
};

export const searchExams = async (searchTerm: string): Promise<Exam[]> => {
  const examsRef = collection(db, 'exams');
  // Firestore não tem busca "LIKE" nativa eficiente. 
  // Para MVP, buscamos os ativos e filtramos no cliente ou usamos um prefixo simples.
  // Aqui vamos pegar os últimos 20 ativos e filtrar no cliente por simplicidade inicial.
  
  // ⚠️ Não limitar a 100 sem ordenação, pois pode "sumir" resultados (ex: PC/PM)
  // quando o conjunto retornado não contém esses editais.
  // Para autocomplete confiável, buscamos todos e filtramos no cliente.
  const snapshot = await getDocs(examsRef);
  const exams = snapshot.docs.map(d => d.data() as Exam);

  // Ordenação e filtro no cliente para garantir case-insensitive e evitar problemas de índice
  const sortedExams = exams.sort((a, b) => b.metadata.year - a.metadata.year);

  if (!searchTerm) return sortedExams.slice(0, 50);

  const normTerm = normalizeText(searchTerm);
  return sortedExams.filter(e => {
    const institutionRaw = e.metadata.institution || "";
    const roleRaw = e.metadata.role || "";
    const bancaRaw = e.metadata.banca || "";

    // Coleta aliases do documento + aliases conhecidos pelo nome da instituição
    const normInstitution = normalizeText(institutionRaw);
    const inferredAliases = INSTITUTION_ALIASES[normInstitution] || [];
    const aliasesRaw = [...(e.metadata.aliases || []), ...inferredAliases];

    // Se for Polícia Civil/PM com estado no nome, herda aliases genéricos
    if (normInstitution.includes("policia civil")) aliasesRaw.push("pc", "policia civil");
    if (normInstitution.includes("policia militar")) aliasesRaw.push("pm", "policia militar");

    const institution = normInstitution;
    const role = normalizeText(roleRaw);
    const banca = normalizeText(bancaRaw);
    const aliases = aliasesRaw.map((a) => normalizeText(a)).filter(Boolean);

    return (
      institution.includes(normTerm) ||
      role.includes(normTerm) ||
      banca.includes(normTerm) ||
      aliases.some(a => a.includes(normTerm))
    );
  });
};

/**
 * Busca um edital específico pelo ID
 */
export const getExamById = async (examId: string): Promise<Exam | null> => {
  const examRef = doc(db, 'exams', examId);
  const snap = await getDoc(examRef);
  
  if (snap.exists()) {
    return snap.data() as Exam;
  }
  return null;
};

/**
 * Gera um ID padronizado para o edital
 */
export const generateExamId = (meta: ExamMetadata): string => {
  const clean = (s: string) => s.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9]/g, '_'); // Substitui especiais por _

  return `${clean(meta.institution)}_${clean(meta.role)}_${meta.year}`;
};

/**
 * Busca TODOS os editais cadastrados (para a página de admin)
 */
export const getAllExams = async (): Promise<Exam[]> => {
  const examsRef = collection(db, 'exams');
  const q = query(examsRef, orderBy('metadata.year', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => d.data() as Exam);
};

/**
 * Atualiza um edital existente no banco de dados
 */
export const updateExam = async (examId: string, data: { [key: string]: any }) => {
  const examRef = doc(db, 'exams', examId);
  // Adiciona o campo de atualização de data automaticamente
  data['updatedAt'] = serverTimestamp(); 
  await updateDoc(examRef, data);
};
